import { useEffect, useRef, useState, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import { firstUnreadId } from "../unread";
import type { ChatMessage, PairingClient } from "../protocol";

/**
 * The selected session's live, decrypted main chat (the newest `chatLimit` messages; the caller grows
 * `chatLimit` on scroll-up). Returns the messages, the first-snapshot `chatReady` flag, and `setChatReady`
 * so the caller's per-session reset can flip it back to loading.
 */
export function useSelectedChat(
  clients: MutableRefObject<Map<string, PairingClient>>,
  selected: string | null,
  chatLimit: number,
  /** From useSessions — re-runs the subscription once the client for this selection exists (see useSessions). */
  clientsVersion: number,
): {
  messages: ChatMessage[];
  chatReady: boolean;
  setChatReady: Dispatch<SetStateAction<boolean>>;
  unreadFromId: string | null;
} {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatReady, setChatReady] = useState(false);
  // Where this chat should open. Captured from the FIRST snapshot and then left alone: displaying a
  // message marks it read, so recomputing would erase the divider in the moment the user looked at it.
  // Carries the chat it belongs to, so the previous chat's anchor is never applied to the next one.
  const [unreadFrom, setUnreadFrom] = useState<{ key: string; id: string | null } | null>(null);
  // Which chats have already been anchored. Paging older messages re-runs this effect and must not
  // re-capture — by then everything on screen is read and the anchor would be lost. It is a SET, not the
  // current chat, because leaving a chat and coming back must not anchor it a second time: the divider marks
  // where you left off when you arrived, and re-deriving it on return threw the reader back up the history
  // they had just read.
  const capturedChats = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!selected) {
      setMessages([]);
      return;
    }
    const [pid, sid] = selected.split("/");
    const client = clients.current.get(pid);
    const unsub = client?.subscribeChat(
      sid,
      (m) => {
        setMessages(m);
        setChatReady(true);
        // Snapshot the anchor BEFORE marking anything read, or the write below removes the very thing
        // being anchored on. Once per chat: later snapshots are arrivals, not where the user left off.
        if (!capturedChats.current.has(selected)) {
          capturedChats.current.add(selected);
          setUnreadFrom({ key: selected, id: firstUnreadId(m) });
        }
        // Displaying them IS reading them — publish the shared read receipt so the phone's badge and
        // notification for this session clear too.
        if (sid) void client.markMessagesRead(sid, m);
      },
      chatLimit,
    );
    return () => unsub?.();
  }, [selected, chatLimit, clientsVersion]);
  return { messages, chatReady, setChatReady, unreadFromId: unreadFrom && unreadFrom.key === selected ? unreadFrom.id : null };
}
